import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { interpolate } from '../lib/content';
import { boundaryPosition } from '../lib/scoring';
import type { Mode, Scenario, UiCopy } from '../lib/types';
import { Verdict } from './Verdict';

interface AssessmentProps {
  ui: UiCopy;
  mode: Mode;
  scenario: Scenario;
  index: number;
  total: number;
  selectedChoiceId: string | null;
  isAdvancing: boolean;
  awaitingContinue: boolean;
  onSelect: (choiceId: string) => void;
  onContinue: () => void;
}

export function Assessment({
  ui,
  mode,
  scenario,
  index,
  total,
  selectedChoiceId,
  isAdvancing,
  awaitingContinue,
  onSelect,
  onContinue,
}: AssessmentProps) {
  const answered = Boolean(selectedChoiceId);
  const progress = ((index + (answered ? 1 : 0)) / total) * 100;
  const marker = selectedChoiceId ? boundaryPosition(scenario, selectedChoiceId) : null;

  // A keyboard answers with the letter or the number it can see on the choice.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (answered || event.metaKey || event.ctrlKey || event.altKey) return;
      const key = event.key.toLowerCase();
      const byLetter = 'abcd'.indexOf(key);
      const byNumber = '1234'.indexOf(key);
      const position = byLetter >= 0 ? byLetter : byNumber;
      const choice = position >= 0 ? scenario.choices[position] : undefined;
      if (!choice) return;
      event.preventDefault();
      onSelect(choice.id);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [answered, onSelect, scenario]);

  return (
    <main className="run">
      <div className="run-head">
        <span className="run-count">
          {interpolate(ui.assessment.questionOf ?? '', { current: index + 1, total })}
        </span>
        <span className="run-role">{ui.modes[mode].label}</span>
      </div>
      <div className="run-track" aria-hidden="true">
        <motion.span
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>

      <motion.div
        key={scenario.id}
        className="run-body"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="run-context">
          <p className="run-eyebrow">{scenario.eyebrow}</p>
          <h1 className="run-scenario">{scenario.scenario}</h1>

          <div className="run-transcript">
            <p><span>{ui.assessment.aiSays}</span>{scenario.aiPrompt}</p>
            <blockquote>{scenario.aiAnswer}</blockquote>
          </div>
        </div>

        <div className="run-decision">
          <h2 className="run-question">{scenario.question}</h2>

          {awaitingContinue && selectedChoiceId ? (
            <Verdict
              ui={ui}
              scenario={scenario}
              choiceId={selectedChoiceId}
              onContinue={onContinue}
            />
          ) : (
            <ul className="run-choices" aria-busy={isAdvancing}>
              {scenario.choices.map((choice, choiceIndex) => (
                <li key={choice.id}>
                  <button
                    type="button"
                    disabled={answered}
                    className={selectedChoiceId === choice.id ? 'is-selected' : ''}
                    onClick={() => onSelect(choice.id)}
                  >
                    <span className="choice-key" aria-hidden="true">
                      {String.fromCharCode(65 + choiceIndex)}
                    </span>
                    <span className="choice-text">{choice.text}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </motion.div>

      <div className="run-scale" aria-hidden="true">
        <span>{ui.assessment.boundaryLeft}</span>
        <i>{marker === null ? null : <b style={{ left: `${marker}%` }} />}</i>
        <span>{ui.assessment.boundaryRight}</span>
      </div>
      <span className="sr-only" role="status">{answered ? ui.assessment.answerRecorded : ''}</span>
    </main>
  );
}
